// グローバルキーボードショートカット。
// - Ctrl + 「+」「-」: 段階ズーム（画面中央が中心）
// - Ctrl + C / X / V: 選択中オブジェクトのコピー / 切り取り / 貼り付け（貼り付けは use-paste-handler）
// - Ctrl + Z / Y: 自分の操作の Undo / Redo
// - Esc: ツール解除（idle へ戻る）
// - Delete / Backspace: 選択中オブジェクトの削除
// - Ctrl + ] / [: 選択中オブジェクトの前面 / 背面移動

import { useEffect } from "react";
import { useShowToast } from "../notice/use-show-toast";
import type { CanvasObject } from "@refinement-canvas/domain";
import { useCanvasStore, type CanvasTool } from "../store/canvas-store";
import { useCanvasDocStore } from "../store/canvas-doc-store";
import { stepZoomIn, stepZoomOut, zoomAt } from "../../../lib/canvas-coordinates";
import { writeCanvasObjectsToClipboardData } from "../../../lib/canvas-clipboard";
import type { CanvasOpDispatcher } from "../use-canvas-op-dispatcher";
import { isTransientAttachmentSrc } from "../attachment-placeholder";

export function useGlobalShortcuts(
  dispatcher?: CanvasOpDispatcher | undefined,
  options?: { readonly readOnly?: boolean | undefined }
): void {
  const setView = useCanvasStore((s) => s.setView);
  const resetMode = useCanvasStore((s) => s.resetMode);
  const showToast = useShowToast();
  // 参照専用（埋め込み）では編集系ショートカットとコピー / 切り取りを一切登録・実行せず、
  // 中央基準のズーム（Ctrl + +/-）だけを残す。
  const readOnly = options?.readOnly ?? false;

  useEffect(() => {
    // 直近の pointerdown がキャンバス面（canvas-container 内）だったか。
    // ミニマップなど canvas 外の「フォーカスを奪わない clickable な chrome」をクリックすると、
    // フォーカスは body のまま残る。body を一律「canvas 面」とみなすと、その直後の単一キーが誤爆する。
    // そこで「直近に操作した面が canvas だったか」を併せて見て、body 経路の誤爆を塞ぐ。
    let lastPointerOnCanvasSurface = false;
    const onPointerDownTrack = (event: PointerEvent): void => {
      lastPointerOnCanvasSurface = isWithinCanvasContainer(event.target);
    };

    const onCopy = (event: ClipboardEvent): void => {
      if (isEditingTarget(event.target) || !event.clipboardData) return;
      const selected = useCanvasStore.getState().selectedObjectIds;
      if (selected.length === 0) return;
      const objects = useCanvasDocStore.getState().objects;
      const copied = selected.map((id) => objects[id]).filter((o): o is NonNullable<typeof o> => Boolean(o));
      if (copied.length === 0) return;
      // アップロード未完了の画像・動画（blob:/data: src）は他参加者が解決できないため、
      // コピー対象から除外する（複製による壊れた画像や永続仮表示の再発を防ぐ）。
      // 混在選択では未完了添付だけを除外し、確定済みのオブジェクトは通常通りコピーする。
      const copyable = copied.filter((o) => !isTransientAttachmentObject(o));
      if (copyable.length === 0) {
        event.preventDefault();
        showToast("warning", "アップロード完了前の画像・動画はコピーできません。完了後にもう一度お試しください。");
        return;
      }
      writeCanvasObjectsToClipboardData(event.clipboardData, copyable);
      event.preventDefault();
      if (copyable.length < copied.length) {
        showToast("info", "アップロード完了前の画像・動画はコピー対象から除外しました。");
      }
    };

    // Ctrl+X: コピーと同じシリアライズを行ったうえで選択中オブジェクトを削除する。
    // 入力中のテキストフィールド側の「文字列の切り取り」操作を奪わないよう、編集対象のフォーカス時は何もしない。
    // ロックされているオブジェクトは削除対象から除外する（クリップボードへは含めて良い）。
    const onCut = (event: ClipboardEvent): void => {
      if (isEditingTarget(event.target) || !event.clipboardData) return;
      const selected = useCanvasStore.getState().selectedObjectIds;
      if (selected.length === 0) return;
      const objects = useCanvasDocStore.getState().objects;
      const copied = selected.map((id) => objects[id]).filter((o): o is NonNullable<typeof o> => Boolean(o));
      if (copied.length === 0) return;
      // コピーと同様に、アップロード未完了の画像・動画は切り取り（=複製＋削除）対象から除外する。
      // 未完了添付はクリップボードへ入れず、削除もせずその場に残す。
      const copyable = copied.filter((o) => !isTransientAttachmentObject(o));
      if (copyable.length === 0) {
        event.preventDefault();
        showToast("warning", "アップロード完了前の画像・動画は切り取りできません。完了後にもう一度お試しください。");
        return;
      }
      writeCanvasObjectsToClipboardData(event.clipboardData, copyable);
      event.preventDefault();
      if (dispatcher) {
        const copyableIds = new Set(copyable.map((o) => o.id));
        // 切り取り対象（=クリップボードへ入れたもの）のうち、ロックされていないものだけ削除する。
        const deletable = selected.filter((id) => copyableIds.has(id) && !objects[id]?.locked);
        if (deletable.length > 0) {
          dispatcher.deleteObjects(deletable);
        }
        useCanvasStore.getState().setSelectedObjectIds([]);
      }
      if (copyable.length < copied.length) {
        showToast("info", "アップロード完了前の画像・動画は切り取り対象から除外しました。");
      }
    };

    const onKeyDown = (event: KeyboardEvent): void => {
      const editingTarget = isEditingTarget(event.target);

      // 参照専用ではズーム（Ctrl + +/-）のみ許可し、編集系ショートカットは一切処理しない。
      if (readOnly) {
        if ((event.ctrlKey || event.metaKey) && !editingTarget) {
          if (event.key === "+" || event.key === ";" || event.key === "=") {
            event.preventDefault();
            const view = useCanvasStore.getState().view;
            const center = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
            setView(zoomAt(view, center, stepZoomIn(view.zoom)));
          } else if (event.key === "-" || event.key === "_") {
            event.preventDefault();
            const view = useCanvasStore.getState().view;
            const center = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
            setView(zoomAt(view, center, stepZoomOut(view.zoom)));
          }
        }
        return;
      }

      if (event.key === "Escape") {
        resetMode();
        return;
      }

      if ((event.key === "Delete" || event.key === "Backspace") && !editingTarget) {
        const selected = useCanvasStore.getState().selectedObjectIds;
        if (selected.length > 0 && dispatcher) {
          // 選択がある時点で「キャンバスへの削除ショートカット」として扱われるため、
          // 実際に削除対象が無い（全部ロック中など）場合でもブラウザ既定動作
          // （例: Backspace による履歴戻り）には流さない。
          event.preventDefault();
          // ロックされているオブジェクトは削除対象から除外する。
          const objects = useCanvasDocStore.getState().objects;
          const deletable = selected.filter((id) => !objects[id]?.locked);
          if (deletable.length > 0) {
            dispatcher.deleteObjects(deletable);
            useCanvasStore.getState().setSelectedObjectIds([]);
          }
        }
        return;
      }

      // 単一キーのツール切替 / グリッド吸着トグル（修飾キーなし・入力中でないときのみ）。
      // C=コメント, F=付箋, N=セクション, R=図形, T=テキスト, M=ペン, E=スタンプ, G=グリッド吸着切替。
      // キー長押しのオートリピートでは多重トグルしないよう event.repeat を除外する。
      // 単一キーは誤爆しやすいため、キャンバス面にフォーカスがあるときだけ有効化する。
      // canvas-container 内にフォーカスがあるとき、または body にフォーカスがあり かつ 直近の操作面が
      // canvas だったときのみとし、ツールバー・コメントメニュー・確認ダイアログ・ミニマップ等の別 UI
      // 操作中の裏でのモード切替を防ぐ（修飾キー付きの既存ショートカットはこの制限を課さない）。
      const onCanvasSurface =
        isWithinCanvasContainer(event.target) ||
        (typeof document !== "undefined" &&
          event.target === document.body &&
          lastPointerOnCanvasSurface);
      if (
        !editingTarget &&
        onCanvasSurface &&
        !event.repeat &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.altKey &&
        !event.shiftKey
      ) {
        // F2: 単一選択中の付箋 / テキストを、ダブルクリックと同じ編集モードへ入れる
        // （Windows のリネーム操作等に倣ったキーボード操作）。編集開始は各オブジェクトの
        // autoEditObjectId effect（配置直後の自動編集と同じ経路）へ委ね、ロック中は effect 側で
        // 無視される。図形ラベル等の他種別は対象外（要望どおり付箋・テキストのみ）。
        // 単一キーのツール切替と同じく onCanvasSurface ゲート内に置き、ツールバー・ダイアログ・
        // コメント UI 等の別 UI 操作中に裏の付箋 / テキストが編集開始されるのを防ぐ。
        if (event.key === "F2") {
          const selected = useCanvasStore.getState().selectedObjectIds;
          const selectedId = selected.length === 1 ? selected[0] : undefined;
          if (selectedId) {
            const target = useCanvasDocStore.getState().objects[selectedId];
            if (target && (target.type === "sticky" || target.type === "text")) {
              event.preventDefault();
              useCanvasStore.getState().setAutoEditObjectId(target.id);
            }
          }
          return;
        }
        const singleKey = event.key.toLowerCase();
        const toolByKey: Partial<Record<string, CanvasTool>> = {
          c: "place-comment",
          f: "place-sticky",
          // セクションは、直感的な S が WASD パン（下移動）と衝突するため N を割り当てる。
          n: "place-section",
          r: "place-shape",
          t: "place-text",
          m: "draw-pen",
          e: "place-stamp",
        };
        const mappedTool = toolByKey[singleKey];
        if (mappedTool !== undefined) {
          event.preventDefault();
          const { tool, setTool } = useCanvasStore.getState();
          // 同じツールキーの再押下で idle へ戻す（ツールバーボタンのトグルと同じ挙動）。
          setTool(tool === mappedTool ? "idle" : mappedTool);
          return;
        }
        if (singleKey === "g") {
          event.preventDefault();
          const { gridSnapEnabled, setGridSnapEnabled } = useCanvasStore.getState();
          setGridSnapEnabled(!gridSnapEnabled);
          return;
        }
        if (singleKey === "h") {
          // 選択操作の左右反転（キャンバス移動 / 範囲選択のボタン入れ替え）をトグルする。
          event.preventDefault();
          const { invertSelectPan, setInvertSelectPan } = useCanvasStore.getState();
          setInvertSelectPan(!invertSelectPan);
          return;
        }
      }

      if (!(event.ctrlKey || event.metaKey) || editingTarget) return;

      const key = event.key.toLowerCase();
      if (key === "z") {
        event.preventDefault();
        dispatcher?.undo();
        return;
      }
      if (key === "y") {
        event.preventDefault();
        dispatcher?.redo();
        return;
      }

      if (event.key === "]") {
        bringForward(dispatcher);
        event.preventDefault();
        return;
      }
      if (event.key === "[") {
        sendBackward(dispatcher);
        event.preventDefault();
        return;
      }

      if (event.key === "+" || event.key === ";" || event.key === "=") {
        // ; や = は Ctrl+= で来る環境がある。いずれもズームインに割り当てる。
        event.preventDefault();
        const view = useCanvasStore.getState().view;
        const center = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
        setView(zoomAt(view, center, stepZoomIn(view.zoom)));
      } else if (event.key === "-" || event.key === "_") {
        event.preventDefault();
        const view = useCanvasStore.getState().view;
        const center = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
        setView(zoomAt(view, center, stepZoomOut(view.zoom)));
      }
    };
    // capture フェーズで拾い、ピン等の stopPropagation より前に直近の操作面を確実に記録する。
    window.addEventListener("pointerdown", onPointerDownTrack, true);
    window.addEventListener("keydown", onKeyDown);
    // 参照専用ではコピー / 切り取りは登録しない（クリップボード経由の複製も無効化する）。
    if (!readOnly) {
      window.addEventListener("copy", onCopy);
      window.addEventListener("cut", onCut);
    }
    return () => {
      window.removeEventListener("pointerdown", onPointerDownTrack, true);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("copy", onCopy);
      window.removeEventListener("cut", onCut);
    };
  }, [dispatcher, readOnly, resetMode, setView, showToast]);
}

// 「アップロード未完了の画像・動画」（blob:/data: src）かどうか。
// コピー/切り取りの除外判定に使う（確定＝公開 URL 後のみ複製を許可する）。
function isTransientAttachmentObject(object: CanvasObject): boolean {
  return (
    (object.type === "image" || object.type === "video") && isTransientAttachmentSrc(object.src)
  );
}

function bringForward(dispatcher: CanvasOpDispatcher | undefined): void {
  if (!dispatcher) return;
  const selected = useCanvasStore.getState().selectedObjectIds;
  if (selected.length === 0) return;
  const objects = useCanvasDocStore.getState().objects;
  // ロック中オブジェクトは zOrder 変更の対象外。複数選択中も locked のものだけ除外する。
  const movable = selected.filter((id) => {
    const object = objects[id];
    return object !== undefined && !object.locked;
  });
  if (movable.length === 0) return;
  const maxZ = Math.max(0, ...Object.values(objects).map((o) => o.zOrder));
  // 1 ショートカット操作 = 1 履歴エントリにまとめ、Ctrl+Z 1 回でグループ全体を戻せるようにする。
  const updates = movable.map((id, index) => ({
    objectId: id,
    transform: { zOrder: maxZ + 1 + index },
  }));
  dispatcher.transformObjects(updates);
}

function sendBackward(dispatcher: CanvasOpDispatcher | undefined): void {
  if (!dispatcher) return;
  const selected = useCanvasStore.getState().selectedObjectIds;
  if (selected.length === 0) return;
  const objects = useCanvasDocStore.getState().objects;
  // ロック中オブジェクトは zOrder 変更の対象外。複数選択中も locked のものだけ除外する。
  const movable = selected.filter((id) => {
    const object = objects[id];
    return object !== undefined && !object.locked;
  });
  if (movable.length === 0) return;
  const minZ = Math.min(0, ...Object.values(objects).map((o) => o.zOrder));
  // 1 ショートカット操作 = 1 履歴エントリにまとめる（前面移動と同様）。
  const updates = movable.map((id, index) => ({
    objectId: id,
    transform: { zOrder: minZ - 1 - index },
  }));
  dispatcher.transformObjects(updates);
}

// テキスト編集中（フォーカスが入力要素・テキストエリア・contentEditable にある）か。
// テキストオブジェクトは contentEditable で実装されるため、isContentEditable も編集中として扱い、
// 編集中の単一キー入力（c/f/r/t/m/e/g 等）がツール切替へ化けて文字入力を奪わないようにする。
function isEditingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName.toLowerCase();
  return tag === "input" || tag === "textarea";
}

// フォーカス/イベント対象がキャンバスコンテナ（canvas-container）内かどうか。
function isWithinCanvasContainer(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest('[data-testid="canvas-container"]') !== null;
}
